import { BASE_URL } from 'config';
import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Button,
  Avatar,
  Typography,
  FormControl,
  FormHelperText,
  Grid,
  Divider,
  Input,
  IconButton,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { validateEmail, validateRequired, validatePasswordMatch } from "validations";
import axios from "axios";
import Swal from "sweetalert2";
import { useMaterialUIController } from "context";
import { useTheme } from "@mui/material/styles";
const AddProductRecommendationsForm = ({ onClose, onSuccess, initialData }) => {
  const [controller] = useMaterialUIController();
  const darkMode = controller.darkMode;
  const theme = useTheme();
  const [formData, setFormData] = useState({
    product_id: initialData?.product_id || "",
    event_id: initialData?.event_id || "",
    location_id: initialData?.location_id || "",
    context_id: initialData?.context_id || "",
    priority: initialData?.priority || "",
    starts_at: initialData?.starts_at || "",
    ends_at: initialData?.ends_at || "",
    id: initialData?.id || null, // Add ID field
  });
  const [products, setProducts] = useState([]);
  const [events, setEvents] = useState([]);
  const [contexts, setContexts] = useState([]);
  const [countries, setCountries] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingDivisions, setLoadingDivisions] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // Add useEffect to handle initial data
  useEffect(() => {
    if (initialData) {
      setFormData({
        product_id: initialData.product_id,
        event_id: initialData.event_id,
        location_id: initialData.location_id,
        context_id: initialData.context_id,
        priority: initialData.priority,
        starts_at: initialData.starts_at,
        ends_at: initialData.ends_at,
        id: initialData.id, // Add ID field
      });
    }
  }, [initialData]);

  useEffect(() => {
    // Fetch categories and contexts from API
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("user-token");
        const response = await axios.get(
          BASE_URL + "/administrator/product-recommendations/create",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setProducts(response.data.data.products || []);
        setEvents(response.data.data.events || []);
        setContexts(response.data.data.contexts || []);
      } catch (error) {
        setProducts([]);
        setEvents([]);
        setContexts([]);
      }
    };
    fetchData();
  }, []);

  // جلب الدول عند فتح الصفحة
  useEffect(() => {
    setLoadingCountries(true);
    const token = localStorage.getItem("user-token");
    axios
      .get(BASE_URL + "/administrator/locations/countries", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setCountries(res.data.data || []);
      })
      .catch(() => setCountries([]))
      .finally(() => setLoadingCountries(false));
  }, []);

  // جلب الولايات عند تغيير الدولة
  useEffect(() => {
    if (!formData.country_id) {
      setDivisions([]);
      setCities([]);
      return;
    }
    setLoadingDivisions(true);
    setDivisions([]);
    setCities([]);
    const token = localStorage.getItem("user-token");
    axios
      .get(
        `${BASE_URL}/administrator/locations/children/${formData.country_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        setDivisions(res.data.data || []);
      })
      .catch(() => setDivisions([]))
      .finally(() => setLoadingDivisions(false));
  }, [formData.country_id]);

  // جلب المدن عند تغيير الولاية
  useEffect(() => {
    if (!formData.division_id) {
      setCities([]);
      return;
    }
    setLoadingCities(true);
    setCities([]);
    const token = localStorage.getItem("user-token");
    axios
      .get(
        `${BASE_URL}/administrator/locations/children/${formData.division_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        setCities(res.data.data || []);
      })
      .catch(() => setCities([]))
      .finally(() => setLoadingCities(false));
  }, [formData.division_id]);

  // دالة تحديث القيم عند اختيار الدولة/الولاية/المدينة
  const handleLocationChange = (type, value) => {
    if (type === "country") {
      setFormData((prev) => ({
        ...prev,
        country_id: value ? value.id : "",
        division_id: "",
        location_id: "",
      }));
    } else if (type === "division") {
      setFormData((prev) => ({ ...prev, division_id: value ? value.id : "", location_id: "" }));
    } else if (type === "city") {
      setFormData((prev) => ({ ...prev, location_id: value ? value.id : "" }));
    }
  };

  const [errors, setErrors] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    if (formSubmitted) validateForm();
  }, [formData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.context_id) newErrors.context_id = "يجب اختيار الشاشة";
    if (!formData.product_id) newErrors.product_id = "يجب اختيار المنتج";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  // make product recommendation
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    if (!validateForm()) return;
    try {
      const formPayload = new FormData();
      // أرسل فقط location_id من الحقول الجغرافية
      const filteredFormData = { ...formData };
      delete filteredFormData.country_id;
      delete filteredFormData.division_id;
      // تأكد من تنسيق الوقت والتاريخ YYYY-MM-DD HH:mm:ss
      if (filteredFormData.starts_at) {
        let s = filteredFormData.starts_at.replace("T", " ");
        if (s.length === 16) s += ":00";
        filteredFormData.starts_at = s;
      }
      if (filteredFormData.ends_at) {
        let e = filteredFormData.ends_at.replace("T", " ");
        if (e.length === 16) e += ":00";
        filteredFormData.ends_at = e;
      }
      Object.entries(filteredFormData).forEach(([key, value]) => {
        if (value !== null && value !== undefined && key !== "id") {
          formPayload.append(key, value);
        }
      });
      Swal.fire({
        title: "جاري الحفظ...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
          const container = document.querySelector(".swal2-container");
          container.style.zIndex = "99999";
        },
      });
      const token = localStorage.getItem("user-token");
      const endpoint = formData.id
        ? `${BASE_URL}/administrator/product-recommendations/update/${formData.id}`
        : BASE_URL + "/administrator/product-recommendations/store";

      const response = await axios.post(endpoint, formPayload, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      Swal.close();
      if (response.data.status) {
        await Swal.fire({
          icon: "success",
          title: "تم الحفظ!",
          text: "تم حفظ البيانات بنجاح",
          confirmButtonText: "موافق",
          didOpen: () => {
            const container = document.querySelector(".swal2-container");
            container.style.zIndex = "99999";
          },
        });
        onSuccess();
        onClose();
      } else {
        throw new Error(response.data.message || "حدث خطأ غير متوقع");
      }
    } catch (error) {
      Swal.close();
      const errorMessage = error.response?.data?.message || error.message || "حدث خطأ أثناء الحفظ";
      Swal.fire({
        icon: "error",
        title: "فشل الحفظ!",
        text: errorMessage,
        confirmButtonText: "موافق",
        didOpen: () => {
          const container = document.querySelector(".swal2-container");
          container.style.zIndex = "99999";
        },
      });
    } finally {
      setFormSubmitted(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      product_id: "",
      event_id: "",
      location_id: "",
      context_id: "",
      priority: "",
      starts_at: "",
      ends_at: "",
      country_id: "",
      division_id: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <Box
      sx={{
        p: 3,
        width: 600,
        bgcolor: darkMode ? theme.palette.background.default : "background.paper",
        color: darkMode ? "white !important" : "inherit",
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          {formData.id ? "تعديل الترشيح" : "إضافة منتج مرشح جديد"}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={6}>
          <FormControl fullWidth error={!!errors.context_id} sx={{ mb: 2 }}>
            <Autocomplete
              options={contexts}
              getOptionLabel={(option) => option.name || ""}
              value={contexts.find((c) => c.id === formData.context_id) || null}
              onChange={(_, newValue) => {
                setFormData((prev) => ({ ...prev, context_id: newValue ? newValue.id : "" }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="اختر الشاشة"
                  error={!!errors.context_id}
                  helperText={errors.context_id}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </FormControl>

          <FormControl fullWidth error={!!errors.category_id} sx={{ mb: 2 }}>
            <Autocomplete
              options={products}
              getOptionLabel={(option) => option.name || ""}
              value={products.find((c) => c.id === formData.product_id) || null}
              onChange={(_, newValue) => {
                setFormData((prev) => ({ ...prev, product_id: newValue ? newValue.id : "" }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="اختر المنتج"
                  error={!!errors.product_id}
                  helperText={errors.product_id}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </FormControl>

          {/* حقل اختيار الفعالية */}
          <FormControl fullWidth error={!!errors.event_id} sx={{ mb: 2 }}>
            <Autocomplete
              options={events}
              getOptionLabel={(option) => option.name || ""}
              value={events.find((c) => c.id === formData.event_id) || null}
              onChange={(_, newValue) => {
                setFormData((prev) => ({ ...prev, event_id: newValue ? newValue.id : "" }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="اختر الفعالية"
                  error={!!errors.event_id}
                  helperText={errors.event_id}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </FormControl>
        </Grid>

        <Grid item xs={6}>
          <FormControl fullWidth error={!!errors.starts_at} sx={{ mb: 2 }}>
            <TextField
              label="تاريخ ووقت البداية"
              type="datetime-local"
              name="starts_at"
              value={formData.starts_at}
              onChange={handleChange}
              error={!!errors.starts_at}
              helperText={errors.starts_at}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 1 }}
            />
          </FormControl>
          <FormControl fullWidth error={!!errors.ends_at} sx={{ mb: 2 }}>
            <TextField
              label="تاريخ ووقت النهاية"
              type="datetime-local"
              name="ends_at"
              value={formData.ends_at}
              onChange={handleChange}
              error={!!errors.ends_at}
              helperText={errors.ends_at}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 1 }}
            />
          </FormControl>
          <FormControl fullWidth error={!!errors.priority} sx={{ mb: 2 }}>
            <TextField
              label="الأولوية"
              type="number"
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              error={!!errors.priority}
              helperText={errors.priority}
            />
          </FormControl>
        </Grid>

        {/* الدولة - الولاية - المدينة */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {/* الدولة */}
            <Grid item xs={4}>
              <FormControl fullWidth>
                <Autocomplete
                  options={countries}
                  loading={loadingCountries}
                  getOptionLabel={(option) => option.name || ""}
                  value={countries.find((c) => c.id === formData.country_id) || null}
                  onChange={(_, newValue) => handleLocationChange("country", newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="الدولة"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCountries && (
                              <span style={{ marginLeft: 8 }}>
                                <span
                                  className="loader"
                                  style={{
                                    display: "inline-block",
                                    width: 16,
                                    height: 16,
                                    border: "2px solid #ccc",
                                    borderTop: "2px solid #1976d2",
                                    borderRadius: "50%",
                                    animation: "spin 1s linear infinite",
                                  }}
                                ></span>
                              </span>
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              </FormControl>
            </Grid>

            {/* الولاية */}
            <Grid item xs={4}>
              <FormControl fullWidth>
                <Autocomplete
                  options={divisions}
                  loading={loadingDivisions}
                  getOptionLabel={(option) => option.name || ""}
                  value={divisions.find((c) => c.id === formData.division_id) || null}
                  onChange={(_, newValue) => handleLocationChange("division", newValue)}
                  disabled={!formData.country_id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="الولاية / المحافظة"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingDivisions && (
                              <span style={{ marginLeft: 8 }}>
                                <span
                                  className="loader"
                                  style={{
                                    display: "inline-block",
                                    width: 16,
                                    height: 16,
                                    border: "2px solid #ccc",
                                    borderTop: "2px solid #1976d2",
                                    borderRadius: "50%",
                                    animation: "spin 1s linear infinite",
                                  }}
                                ></span>
                              </span>
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              </FormControl>
            </Grid>

            {/* المدينة */}
            <Grid item xs={4}>
              <FormControl fullWidth>
                <Autocomplete
                  options={cities}
                  loading={loadingCities}
                  getOptionLabel={(option) => option.name || ""}
                  value={cities.find((c) => c.id === formData.location_id) || null}
                  onChange={(_, newValue) => handleLocationChange("city", newValue)}
                  disabled={!formData.division_id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="المدينة / الحي"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCities && (
                              <span style={{ marginLeft: 8 }}>
                                <span
                                  className="loader"
                                  style={{
                                    display: "inline-block",
                                    width: 16,
                                    height: 16,
                                    border: "2px solid #ccc",
                                    borderTop: "2px solid #1976d2",
                                    borderRadius: "50%",
                                    animation: "spin 1s linear infinite",
                                  }}
                                ></span>
                              </span>
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Bottom Actions */}
      <Box mt={4} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="textSecondary">
          * الحقول المطلوبة
        </Typography>
        <Box display="flex" gap={2}>
          <Button variant="contained" color="error" onClick={handleCancel} sx={{ px: 4 }}>
            إلغاء
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ px: 4, color: "white !important" }}
            onClick={handleSubmit}
          >
            حفظ
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AddProductRecommendationsForm;
